/**
 * Delad palett för rundans display_color (S-04-24).
 *
 * Backend lagrar kortnamn (lowercase, svenska): backend/V202605050001
 * + Hearth.ALLOWED_DISPLAY_COLORS. Klienten översätter kortnamn till
 *  hex-värden och rubriker via funktionerna här. Inga hex-värden ligger
 * i schemat eller audit-payload — admin väljer från swatch-grid.
 *
 * Hard-rules (CLAUDE.md):
 *  - Ingen blå/grön status. Endast varma toner.
 *  - Inga fritextfält. Admin kan inte mata in egen hex.
 *  - Designsystem-tokens — hex-värdena här definieras EN gång och
 *    delas mellan HearthsView form, RitaMapView render,
 *    HearthViewModal metadata och RouteMapPreview render.
 */

export type RouteDisplayColor =
  | 'barnsten'
  | 'glod'
  | 'sand'
  | 'skymning'
  | 'lera'
  | 'aska'
  | 'eld'
  | 'tra';

/**
 * Palett-listan i visningsordning. Första (\`barnsten\`) fungerar som
 * default-känsla — varmgul-orange matchande Härdens primärfärg.
 */
export const ROUTE_DISPLAY_COLORS: ReadonlyArray<RouteDisplayColor> = [
  'barnsten',
  'glod',
  'sand',
  'skymning',
  'lera',
  'aska',
  'eld',
  'tra',
] as const;

const HEX_BY_COLOR: Record<RouteDisplayColor, string> = {
  barnsten: '#C4501A',
  glod: '#E89B6F',
  sand: '#D8B795',
  skymning: '#8B3A28',
  lera: '#9C2F1F',
  aska: '#5C5247',
  eld: '#E66424',
  tra: '#5A3A1F',
};

const LABEL_BY_COLOR: Record<RouteDisplayColor, string> = {
  barnsten: 'Bärnsten',
  glod: 'Glöd',
  sand: 'Sand',
  skymning: 'Skymning',
  lera: 'Lera',
  aska: 'Aska',
  eld: 'Eld',
  tra: 'Trä',
};

/**
 * Mappar kortnamn till svensk visningsrubrik. Används i form-swatches,
 * metadata-grid och tooltips.
 */
export function labelForColor(color: RouteDisplayColor): string {
  return LABEL_BY_COLOR[color];
}

/**
 * Hex-värde för MapLibre line-color, CSS background och swatch-render.
 */
export function hexForColor(color: RouteDisplayColor): string {
  return HEX_BY_COLOR[color];
}

/**
 * Type guard som även accepterar null/undefined och okända strängar.
 * Returnerar typ-säker `RouteDisplayColor` eller null.
 */
export function asRouteDisplayColor(value: unknown): RouteDisplayColor | null {
  if (typeof value !== 'string') return null;
  if (ROUTE_DISPLAY_COLORS.includes(value as RouteDisplayColor)) {
    return value as RouteDisplayColor;
  }
  return null;
}

/**
 * Render-färg för en runda baserat på display_color med priority-baserad
 * fallback om värdet är null. Återanvänds i RouteMapPreview, RitaMapView
 * och HearthsView listrad-prick. Detta är den enda funktionen som
 * känner till priority-fallback-mappningen — andra komponenter ska bara
 * läsa från den.
 */
export function renderColorFor(
  displayColor: string | null,
  priority: 'low' | 'medium' | 'high',
): string {
  const color = asRouteDisplayColor(displayColor);
  if (color !== null) return hexForColor(color);
  // Priority-fallback matchar tidigare RouteMapPreview-värden för
  // backward-compat (existerande rundor utan display_color).
  switch (priority) {
    case 'high':
      return '#E66424';
    case 'medium':
      return '#C4501A';
    case 'low':
      return '#A57B5A';
  }
}
